import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Share2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ShareTaskModal - Lightweight modal for sharing a task
 * 
 * Generates a share link and allows:
 * - Copy text: "[User] is working on: [Task Name]"
 * - Copy link: Share URL
 */
const ShareTaskModal = ({ isOpen, onClose, task, userName }) => {
  const [loading, setLoading] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [error, setError] = useState(null);
  const [copiedText, setCopiedText] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Generate share link when modal opens
  React.useEffect(() => {
    if (isOpen && task && !shareData && !error) {
      generateShareLink();
    }
  }, [isOpen, task]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setShareData(null);
      setError(null);
      setCopiedText(false);
      setCopiedLink(false);
    }
  }, [isOpen]);

  const generateShareLink = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      };
      
      const response = await fetch(`${API_URL}/api/tasks/share`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          task_name: task.content,
          task_id: task.id
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('sign_in');
        } else {
          console.error('Share failed:', response.status, await response.text());
          setError('failed');
        }
        return;
      }

      const data = await response.json();
      setShareData(data);
    } catch (err) {
      console.error('Share error:', err);
      setError('failed');
    } finally {
      setLoading(false);
    }
  };

  const copyText = async () => {
    if (!shareData) return;
    try {
      await navigator.clipboard.writeText(shareData.share_text);
      setCopiedText(true);
      setTimeout(() => setCopiedText(false), 2000);
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const copyLink = async () => {
    if (!shareData) return;
    try {
      await navigator.clipboard.writeText(shareData.share_url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      toast.success('Link copied');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const nativeShare = async () => {
    if (!shareData) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Where Was I',
          text: shareData.share_text,
          url: shareData.share_url
        });
      } catch (err) {
        // User cancelled or error - silent fail
      }
    } else {
      // Fallback to copy link
      copyLink();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-4 top-[40%] -translate-y-1/2 mx-auto max-w-sm bg-white rounded-2xl z-50 overflow-hidden"
            style={{ 
              boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
              <h2 className="text-[15px] font-medium text-[#27272A]">Share this</h2>
              <button
                onClick={onClose}
                className="p-1.5 text-[#A1A1AA] hover:text-[#52525B] hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="px-4 py-3">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 border-2 border-[#0CBCBF] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : error ? (
                <div className="text-center py-4">
                  {error === 'sign_in' ? (
                    <>
                      <p className="text-[#52525B] mb-3">Sign in to share tasks</p>
                      <button
                        onClick={() => {
                          onClose();
                          const signInBtn = document.querySelector('[data-testid="sign-in-button"]');
                          if (signInBtn) signInBtn.click();
                        }}
                        className="px-5 py-2 bg-[#0CBCBF] hover:bg-[#0AA8B3] text-white rounded-full text-sm font-medium transition-colors"
                      >
                        Sign in
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-[#52525B] mb-3">Unable to create share link</p>
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={generateShareLink}
                          className="px-5 py-2 bg-[#F6FAFA] hover:bg-[#E8F7F7] text-[#0CBCBF] rounded-full text-sm font-medium transition-colors"
                        >
                          Try again
                        </button>
                        <button
                          onClick={() => {
                            // Fallback: share task text directly
                            const text = `Working on: ${task?.content}\n\n— via Get Where Was I`;
                            if (navigator.share) {
                              navigator.share({ text });
                            } else {
                              navigator.clipboard.writeText(text);
                              toast.success('Copied to clipboard');
                            }
                            onClose();
                          }}
                          className="px-5 py-2 bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-full text-sm font-medium transition-colors"
                        >
                          Share text only
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : shareData ? (
                <div className="space-y-3">
                  {/* Preview - just the task, centered */}
                  <div className="text-center py-2">
                    <p className="text-[#A1A1AA] text-xs mb-1">They'll see</p>
                    <p className="text-[#27272A] text-base font-normal">
                      {task?.content}
                    </p>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    {/* Copy Text */}
                    <button
                      onClick={copyText}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 bg-[#F6FAFA] hover:bg-[#E8F7F7] text-[#0CBCBF] rounded-xl text-sm font-medium transition-colors"
                    >
                      {copiedText ? (
                        <>
                          <Check size={16} />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy size={16} />
                          Copy Text
                        </>
                      )}
                    </button>

                    {/* Copy Link */}
                    <button
                      onClick={copyLink}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 bg-[#F6FAFA] hover:bg-[#E8F7F7] text-[#0CBCBF] rounded-xl text-sm font-medium transition-colors"
                    >
                      {copiedLink ? (
                        <>
                          <Check size={16} />
                          Copied
                        </>
                      ) : (
                        <>
                          <Link2 size={16} />
                          Copy Link
                        </>
                      )}
                    </button>
                  </div>

                  {/* Native share button (mobile) */}
                  {'share' in navigator && (
                    <button
                      onClick={nativeShare}
                      className="w-full flex items-center justify-center gap-2 py-2.5 px-3 bg-[#0CBCBF] hover:bg-[#0AA8B3] text-white rounded-xl text-sm font-medium transition-colors"
                    >
                      <Share2 size={16} />
                      Share
                    </button>
                  )}

                  {/* Note */}
                  <p className="text-xs text-[#C4C4C4] text-center pb-1">
                    View only. No editing or access.
                  </p>
                </div>
              ) : null}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ShareTaskModal;
